import { X } from 'lucide-react'

interface Props {
  qualification: Record<string, string>
  onClose: () => void
}

/** Map HubSpot internal property names to friendly labels */
const LABELS: Record<string, string> = {
  dealname: 'Deal Name',
  amount: 'Amount',
  dealstage: 'Deal Stage',
  pipeline: 'Pipeline',
  dealtype: 'Deal Type',
  closedate: 'Close Date',
  createdate: 'Created',
  hs_lastmodifieddate: 'Last Modified',
  hubspot_owner_id: 'Owner ID',
  hs_forecast_amount: 'Forecast Amount',
  hs_forecast_probability: 'Forecast Probability',
  hs_is_closed: 'Closed',
  hs_is_closed_won: 'Closed Won',
  product: 'Product',
  products_in_deal: 'Products in Deal',
  product_countries: 'Production Countries',
  kyc_ruleset: 'KYC Ruleset',
  reporting_entity_: 'Reporting Entity',
  est_monthly_kyc_checks_cloned_: 'Est. Monthly KYC Checks',
  presales_engineer: 'Pre-Sales Engineer',
  solutions_consultant_owner: 'Solutions Consultant',
  presales_status: 'Pre-Sales Status',
  presales_engagement_date: 'Engagement Date',
  presales_demo_count: 'Demo Count',
  total_presales_hours: 'Total Pre-Sales Hours',
  handover_to_implementations_date: 'Handover Date',
  num_associated_contacts: 'Associated Contacts',
  num_notes: 'Notes Count',
  hs_object_id: 'HubSpot ID',
}

/** Properties to show first, in this order */
const PRIORITY_KEYS = [
  'dealname', 'product', 'products_in_deal', 'kyc_ruleset',
  'reporting_entity_', 'product_countries', 'est_monthly_kyc_checks_cloned_',
  'amount', 'hs_forecast_amount', 'dealstage', 'pipeline', 'dealtype',
  'closedate', 'presales_engineer', 'solutions_consultant_owner',
  'presales_status', 'presales_engagement_date', 'total_presales_hours',
  'presales_demo_count', 'handover_to_implementations_date',
]

/** Keys to hide (internal HubSpot metadata) */
const HIDDEN_KEYS = new Set(['hs_object_id', 'hs_lastmodifieddate', 'createdate'])

function formatLabel(key: string): string {
  if (LABELS[key]) return LABELS[key]
  return key
    .replace(/^hs_/, '')
    .replace(/_+$/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
}

function formatValue(key: string, value: string): string {
  // Semicolon-separated multi-selects
  if (value.includes(';')) return value.split(';').join(', ')

  // Booleans
  if (value === 'true') return 'Yes'
  if (value === 'false') return 'No'

  // Dates (ISO format)
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    try {
      return new Date(value).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
    } catch { return value }
  }

  // Numbers that look like currency
  if (key.includes('amount') || key === 'amount') {
    const num = Number(value)
    if (!isNaN(num)) return `$${num.toLocaleString()}`
  }

  // Percentage
  if (key.includes('probability')) {
    const num = Number(value)
    if (!isNaN(num)) return num <= 1 ? `${(num * 100).toFixed(0)}%` : `${num}%`
  }

  // Generic numbers
  const num = Number(value)
  if (!isNaN(num) && value.trim() !== '') return num.toLocaleString()

  return value
}

export function QualificationModal({ qualification, onClose }: Props) {
  // Sort: priority keys first, then remaining alphabetically
  const allKeys = Object.keys(qualification).filter(k => !HIDDEN_KEYS.has(k))
  const priorityPresent = PRIORITY_KEYS.filter(k => allKeys.includes(k))
  const remaining = allKeys.filter(k => !PRIORITY_KEYS.includes(k)).sort()
  const sortedKeys = [...priorityPresent, ...remaining]

  // Split into qualification section and deal info section
  const qualKeys = ['product', 'products_in_deal', 'kyc_ruleset', 'reporting_entity_', 'product_countries', 'est_monthly_kyc_checks_cloned_']
  const qualSection = sortedKeys.filter(k => qualKeys.includes(k))
  const otherSection = sortedKeys.filter(k => !qualKeys.includes(k))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-color)]">
          <div>
            <h3 className="text-base font-semibold text-[var(--text-primary)]">HubSpot Deal Properties</h3>
            <p className="text-xs text-[var(--text-muted)] mt-0.5">
              {sortedKeys.length} properties • HubSpot deal #{qualification.hs_object_id || '—'}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[var(--bg-tertiary)] rounded-lg transition-colors">
            <X size={18} className="text-[var(--text-muted)]" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Qualification section */}
          {qualSection.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-indigo-400 mb-3">Qualification</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {qualSection.map(key => (
                  <div key={key} className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg px-3 py-2">
                    <div className="text-[10px] uppercase text-indigo-400/60 tracking-wider">{formatLabel(key)}</div>
                    <div className="text-sm text-[var(--text-primary)] mt-0.5">{formatValue(key, qualification[key])}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* All other deal properties */}
          {otherSection.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-[var(--text-muted)] mb-3">Deal Properties</h4>
              <div className="border border-[var(--border-color)] rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[var(--bg-secondary)]">
                      <th className="text-left px-3 py-2 text-[var(--text-muted)] font-medium w-48">Property</th>
                      <th className="text-left px-3 py-2 text-[var(--text-muted)] font-medium">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {otherSection.map(key => (
                      <tr key={key} className="border-t border-[var(--border-color)]">
                        <td className="px-3 py-2 text-[var(--text-muted)]">{formatLabel(key)}</td>
                        <td className="px-3 py-2 text-[var(--text-primary)]">{formatValue(key, qualification[key])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[var(--border-color)] flex justify-end">
          <button
            type="button"
            className="px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] rounded-lg text-sm font-medium hover:bg-[var(--bg-secondary)] transition-colors"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
